// Session persistence for the PDF editor.
//
// Mirrors the image editor's pattern (lib/image-editor/session-store.ts):
// every open tab — the pristine original PDF bytes plus the annotation
// list layered on top — is autosaved to IndexedDB so a refresh or crash
// doesn't lose work, and previously loaded PDFs come back as tabs on
// the next visit. localStorage can't hold PDFs (5 MB string quota);
// IndexedDB stores the ArrayBuffers natively. All access is inside
// these functions, so the module is safe to import during SSR.

import type { Annotation, FormValues } from "./protocol.ts";

export interface StoredDoc {
  id: string;
  name: string;
  /** The unmodified PDF as uploaded/created — saves re-apply annotations
   *  to these, so they must stay pristine. */
  bytes: ArrayBuffer;
  annotations: Annotation[];
  /** User-entered AcroForm values (absent in pre-forms sessions). */
  formValues?: FormValues;
}

export interface SessionData {
  version: 1;
  savedAt: number;
  activeId: string;
  docs: StoredDoc[];
}

const DB_NAME = "xtract-pdf-editor";
const STORE = "session";
const KEY = "current";
const DB_VERSION = 1;

// Refuse to persist truly huge documents rather than hammer the quota;
// the tab still works for the session, it just won't survive a reload.
export const MAX_PERSIST_BYTES = 100 * 1024 * 1024;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function tx<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
      })
  );
}

export async function saveSession(data: SessionData): Promise<void> {
  try {
    await tx("readwrite", (s) => s.put(data, KEY));
  } catch {
    // Best-effort: private-mode / quota / disabled IDB shouldn't break
    // the editor.
  }
}

export async function loadSession(): Promise<SessionData | null> {
  try {
    const data = await tx<SessionData | undefined>("readonly", (s) => s.get(KEY));
    return data && data.docs?.length ? data : null;
  } catch {
    return null;
  }
}

export async function clearSession(): Promise<void> {
  try {
    await tx("readwrite", (s) => s.delete(KEY));
  } catch {
    /* ignore */
  }
}
